import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { IClientFetch } from '@/features/clients/models/client-schema'
import type { IEquityStatement } from '@/features/clients/models/equity-statement'
import type { PepGeneral } from '@/shared/schemas/pep'
import type { LegalRepresentative } from '../../clients/models/legal-representative'
import {
  INITIAL_APPLICANT_INDIVIDUAL,
  INITIAL_ECONOMIC_INFO_INDIVIDUAL,
  INITIAL_PEP_CLIENT
} from '../lib/inital-data'
import type { ApplicantIndividual, ApplicantIndividualDraft } from '../models/individual-schema'
import type { IEconomicInfo } from '../models/info-economic-schema'

export interface IPersonIndividualFetch {
  client: IClientFetch
  applicant: ApplicantIndividual
  pep: PepGeneral
  legalRepresentatives: LegalRepresentative[]
  economicInformation: IEconomicInfo
  equityStatement: IEquityStatement | null
  photo: string | null
}

/** RENAP verification snapshot captured during the lookup (source of truth). */
export interface RenapVerificationState {
  cui: string
  verifiedAt: string
  fields: string[]
}

interface IndividualState {
  // El store guarda el BORRADOR del formulario (enums pueden estar sin
  // seleccionar); el tipo estricto ApplicantIndividual sólo existe
  // post-validación zod.
  applicant: ApplicantIndividualDraft
  setApplicant: (applicant: ApplicantIndividualDraft) => void
  pep: PepGeneral
  setPep: (pep: PepGeneral) => void
  legalRepresentatives: LegalRepresentative[]
  setLegalRepresentatives: (legalRepresentative: LegalRepresentative[]) => void
  economicForm: IEconomicInfo
  setEconomicForm: (economicForm: IEconomicInfo) => void
  equityStatement: IEquityStatement | null
  setEquityStatement: (equityStatement: IEquityStatement | null) => void
  photo: string | null
  setPhoto: (data: string | null) => void
  renapVerification: RenapVerificationState | null
  setRenapVerification: (data: RenapVerificationState | null) => void
  clearData: () => void
}

export const useIndividualStore = create<IndividualState>()(
  persist(
    (set, get) => ({
      photo: null,
      setPhoto: (data) => {
        set(() => ({ photo: data }))
      },
      applicant: INITIAL_APPLICANT_INDIVIDUAL,
      setApplicant: (data) => {
        set(() => ({ applicant: { ...data } }))
      },
      pep: INITIAL_PEP_CLIENT,
      setPep: (data) => {
        set(() => ({ pep: { ...data } }))
      },
      legalRepresentatives: [],
      setLegalRepresentatives: (data) => {
        set(() => ({ legalRepresentatives: [...data] }))
      },
      economicForm: INITIAL_ECONOMIC_INFO_INDIVIDUAL,
      setEconomicForm: (data) => {
        set(() => ({ economicForm: { ...data } }))
      },
      equityStatement: null,
      setEquityStatement: (data) => {
        set(() => ({ equityStatement: data ? { ...data } : null }))
      },
      renapVerification: null,
      setRenapVerification: (data) => {
        set(() => ({ renapVerification: data }))
      },
      // CLEAR DATA
      clearData: () => {
        set(() => ({
          applicant: INITIAL_APPLICANT_INDIVIDUAL,
          pep: INITIAL_PEP_CLIENT,
          legalRepresentatives: [],
          economicForm: INITIAL_ECONOMIC_INFO_INDIVIDUAL,
          equityStatement: null,
          photo: null,
          renapVerification: null
        }))
      }
    }),
    {
      name: 'individual'
    }
  )
)
